import React, { createContext, useContext, ReactNode } from 'react';
import { tokens } from '../styles/tokens';
import { useColorMode } from './ColorModeContext'; // Assuming ColorModeContext is TSX

// Define the shape of the theme object
interface ThemeContextType {
  mode: 'light' | 'dark';
  // Include other properties from tokens and components if needed for type safety
  // For brevity, using 'any' for complex nested objects, but ideally type these
  colors: any;
  spacing: any;
  typography: any;
  borderRadius: any;
  shadows: any;
  components: any; 
  toggleTheme: () => void;
  isDark: boolean;
  isLight: boolean;
}

// Create the theme context with a default value (can be null or a default theme)
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) { // Check for undefined instead of falsy
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Define props for the provider
interface ThemeProviderProps {
  children: ReactNode;
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get color mode from ColorModeContext
  // Assuming useColorMode provides correctly typed values
  const { colorMode, toggleColorMode } = useColorMode();
  
  // Create theme object with all tokens and utility functions
  const themeObj: ThemeContextType = {
    mode: colorMode,
    ...tokens, // Spread tokens directly (ensure tokens object matches expected structure)
    components: { // Keep this explicit part for clarity or type safety
      card: {
        background: tokens.colors.background.secondary,
        border: tokens.colors.border.default,
        shadow: colorMode === 'dark' ? tokens.shadows.lg : tokens.shadows.md,
        radius: tokens.borderRadius.lg,
      },
      button: {
        primary: {
          background: tokens.colors.primary[500],
          text: colorMode === 'dark' ? tokens.colors.neutral[50] : tokens.colors.neutral[50],
          hover: tokens.colors.primary[600],
          active: tokens.colors.primary[700],
          disabled: tokens.colors.primary[300],
        },
        secondary: {
          background: colorMode === 'dark' ? tokens.colors.neutral[700] : tokens.colors.neutral[200],
          text: colorMode === 'dark' ? tokens.colors.neutral[100] : tokens.colors.neutral[800],
          hover: colorMode === 'dark' ? tokens.colors.neutral[600] : tokens.colors.neutral[300],
          active: colorMode === 'dark' ? tokens.colors.neutral[800] : tokens.colors.neutral[400],
          disabled: colorMode === 'dark' ? tokens.colors.neutral[800] : tokens.colors.neutral[200],
        },
      },
      input: {
        background: colorMode === 'dark' ? tokens.colors.neutral[800] : tokens.colors.neutral[50],
        border: tokens.colors.border.default,
        text: tokens.colors.text.primary,
        placeholder: tokens.colors.text.tertiary,
        focus: tokens.colors.border.focus,
      },
    },
    toggleTheme: toggleColorMode,
    isDark: colorMode === 'dark',
    isLight: colorMode === 'light',
  };
  
  return (
    <ThemeContext.Provider value={themeObj}>
      {children}
    </ThemeContext.Provider>
  );
};

// Default export might not be needed if only named exports are used
// export default ThemeContext; 